import React, { useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Link, Loader2, X } from "lucide-react";
import { UrlContext } from "../../../contexts/UrlContext";

const DATABASE_TIMEZONE = "Europe/Berlin";

function safeDate(value) {
  if (!value) return null;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateParts(startValue, endValue) {
  const start = safeDate(startValue);
  const end = safeDate(endValue);

  if (!start) {
    return {
      weekday: "-",
      day: "-",
      monthYear: "-",
      longDate: "No date available",
      timeRange: "-",
    };
  }

  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const longDateFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const timeFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = dateFormatter.formatToParts(start);
  const getPart = (type) => parts.find((part) => part.type === type)?.value || "-";

  return {
    weekday: getPart("weekday"),
    day: getPart("day"),
    monthYear: `${getPart("month")} ${getPart("year")}`,
    longDate: longDateFormatter.format(start),
    timeRange: end
      ? `${timeFormatter.format(start)} - ${timeFormatter.format(end)}`
      : timeFormatter.format(start),
  };
}

function timeLabel(value) {
  const date = safeDate(value);

  if (!date) return "-";

  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

export default function CallDetails({
  projectID,
  expertID,
  setIsViewEventDetails,
  clickingOnProfile,
}) {
  const { API_URL } = useContext(UrlContext);

  const [loading, setLoading] = useState(false);
  const [event, setEvent] = useState(null);
  const [clientEvent, setClientEvent] = useState(null);
  const [clientAttendees, setClientAttendees] = useState([]);
  const [isCopying, setIsCopying] = useState(false);
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const lastTrackedMeetingID = useRef(null);

  const closeModal = () => {
    setIsViewEventDetails(false);
  };

  useEffect(() => {
    document.body.classList.add("overflow-hidden");

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  useEffect(() => {
    getTeamsCallDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTeamsCallDetails = async () => {
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}get-latest-teams-call-details`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          projectID,
          expertID,
        }),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        setEvent(responseData.teamsCallDetails || null);
      } else {
        console.error("failed to get tracking data");
      }
    } catch (error) {
      console.error("Error while calling the backend API:", error);
    } finally {
      setLoading(false);
    }
  };

  const trackEventAttendees = async (meetingID) => {
    if (!meetingID) return;

    const authToken = localStorage.getItem("accesstoken");

    try {
      const response = await fetch(`${API_URL}track-event-attendees`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          accessToken: authToken,
          meetingID,
        }),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        setClientAttendees(responseData.clientEvent?.attendees || []);
      } else {
        console.error("failed to get tracking data");
      }
    } catch (error) {
      console.error("Error while calling the backend API:", error);
    }
  };

  const getEvents = async (meetingID) => {
    if (!meetingID) return;

    try {
      const response = await fetch(`${API_URL}get-client-expert-events`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          accessToken: null,
          meetingID,
        }),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        setClientEvent(responseData.clientEvent || null);
      } else {
        console.error("failed to get tracking data");
      }
    } catch (error) {
      console.error("Error while calling the backend API:", error);
    }
  };

  useEffect(() => {
    if (event?.meetingID && event.meetingID !== lastTrackedMeetingID.current) {
      lastTrackedMeetingID.current = event.meetingID;
      trackEventAttendees(event.meetingID);
      getEvents(event.meetingID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.meetingID]);

  const copyMeetingLink = async () => {
    if (!event?.meetingID) return;

    setIsCopying(true);
    setCopyFailed(false);

    const authToken = localStorage.getItem("accesstoken");

    try {
      const response = await fetch(`${API_URL}copy-meeting-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accessToken: authToken,
          meetingID: event.meetingID,
        }),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        const meetingLink = responseData.meetingLink || "";

        if (navigator.clipboard?.write && window.ClipboardItem) {
          await navigator.clipboard.write([
            new ClipboardItem({
              "text/html": new Blob([meetingLink], { type: "text/html" }),
              "text/plain": new Blob([meetingLink.replace(/<[^>]+>/g, " ")], {
                type: "text/plain",
              }),
            }),
          ]);
        } else {
          await navigator.clipboard.writeText(meetingLink.replace(/<[^>]+>/g, " "));
        }

        setIsLinkCopied(true);
        setTimeout(() => {
          setIsLinkCopied(false);
        }, 2000);

        if (clickingOnProfile) {
          clickingOnProfile(expertID, "Client copied Teams meeting joining link");
        }
      } else {
        setCopyFailed(true);

        if (clickingOnProfile) {
          clickingOnProfile(
            expertID,
            "Client could not copy Teams meeting joining link"
          );
        }
      }
    } catch (error) {
      console.error("Copy failed:", error);
      setCopyFailed(true);

      if (clickingOnProfile) {
        clickingOnProfile(
          expertID,
          "Client could not copy Teams meeting joining link"
        );
      }
    } finally {
      setIsCopying(false);
    }
  };

  const startValue = event?.startTime || event?.start;
  const endValue = event?.endTime || event?.end;
  const time = formatDateParts(startValue, endValue);

  const weekday = time.weekday;
  const day = time.day;
  const monthYear = time.monthYear;

  const clientInviteBody = clientEvent?.body || null;

  return (
    <>
      <motion.div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 no-print">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          className="flex h-[calc(100vh-100px)] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.22)]"
        >
          {/* Fixed title */}
          <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-3">
            <div>
              <p className="text-lg font-semibold text-[#365561]">
                {event?.inviteTitle || event?.title || "Teams Call Details"}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {time.longDate}, {time.timeRange}
              </p>
            </div>

            <button
              type="button"
              onClick={closeModal}
              className="rounded-2xl border border-slate-200 bg-white p-2 text-slate-500 transition hover:bg-slate-50 focus:outline-none"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {loading ? (
            <div className="flex min-h-0 flex-1 items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <i className="fa-solid fa-spinner fa-spin text-4xl text-[#365561]" />
                <p className="text-sm font-semibold text-slate-500">
                  Loading Teams call details...
                </p>
              </div>
            </div>
          ) : !event ? (
            <div className="flex min-h-0 flex-1 items-center justify-center p-6 text-center">
              <div>
                <p className="text-sm font-semibold text-slate-700">
                  No Teams call details found.
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  There is no latest Teams call for this project and expert yet.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid min-h-0 flex-1 gap-8 overflow-hidden px-5 lg:grid-cols-[240px_minmax(0,1fr)]">
              {/* Left fixed section */}
              <div className="hidden items-start no-print lg:flex">
                <div className="w-full space-y-3">
                  <div className="mb-4 mt-4 text-left text-[11px] leading-5 text-slate-400">
                    <p>
                      Transcripts and recordings are available only for recorded
                      meetings.
                    </p>
                  </div>

                  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                    <div className="bg-[#365561] px-4 py-4 text-center">
                      <p className="text-md font-bold uppercase tracking-[0.18em] text-white">
                        {monthYear}
                      </p>
                    </div>

                    <div className="px-4 py-4 text-center">
                      <p className="text-sm font-medium uppercase tracking-[0.14em] text-slate-400">
                        {weekday}
                      </p>
                      <p className="mt-1 text-7xl font-semibold leading-none text-slate-900">
                        {day}
                      </p>
                      <span className="flex flex-row items-center justify-center gap-1 mt-3 font-semibold">
                        <p className="text-sm text-slate-700">
                          {timeLabel(startValue)} - {timeLabel(endValue)},
                        </p>
                        <p className="text-xs text-slate-400">Europe/Berlin</p>
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center gap-2 px-1 text-sm">
                    <p className="font-semibold text-slate-600">
                      Copy meeting link
                    </p>

                    <button
                      type="button"
                      onClick={copyMeetingLink}
                      disabled={isCopying}
                      className={[
                        "inline-flex h-7 w-7 items-center justify-center rounded-full transition focus:outline-none",
                        isLinkCopied
                          ? "bg-emerald-50 text-emerald-600"
                          : copyFailed
                            ? "bg-rose-50 text-rose-500"
                            : "text-[#365561] hover:bg-[#365561]/10",
                        isCopying ? "cursor-wait opacity-80" : "cursor-pointer",
                      ].join(" ")}
                      title="Copy meeting link"
                    >
                      {isCopying ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : isLinkCopied ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : (
                        <Link className="h-4 w-4" />
                      )}
                    </button>

                    <div
                      className={[
                        "transition-opacity duration-300 ease-in-out",
                        isLinkCopied || copyFailed ? "opacity-100" : "opacity-0",
                      ].join(" ")}
                    >
                      {isLinkCopied ? (
                        <span className="text-xs font-semibold text-emerald-600">
                          Copied!
                        </span>
                      ) : copyFailed ? (
                        <span className="text-xs font-semibold text-rose-500">
                          Failed
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="px-1 text-left text-xs leading-5 text-slate-500">
                    <p>
                      Created by{" "}
                      <span className="font-semibold text-slate-700">
                        {event.createdBy || event.owner || "Unknown"}
                      </span>
                    </p>

                    <p>
                      Created for{" "}
                      <span className="font-semibold text-slate-700">
                        {event.clientName || "Unknown"}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Right scrollable/switching section */}
              <div className="min-h-0 overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.div
                    key="overview"
                    initial={{ opacity: 0, x: 28 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -28 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                    className="h-full overflow-y-auto pr-1"
                  >
                    <div className="mb-4 mt-4 space-y-4">
                      <div className="grid grid-cols-1 gap-3">
                        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                          <p className="text-sm font-semibold text-slate-900">
                            Meeting invite details
                          </p>

                          <div className="mt-3">
                            <div
                              className="
                                mt-3 rounded-xl border border-slate-500 bg-slate-50 p-3
                                text-xs leading-relaxed text-slate-700
                                [&_*]:!text-xs
                                [&_*]:!leading-relaxed
                                [&_h1]:!text-base
                                [&_h2]:!text-sm
                                [&_h3]:!text-sm
                                [&_a]:!text-xs
                                [&_a]:!text-blue-600
                                [&_a]:!underline
                                [&_a]:break-all
                              "
                              dangerouslySetInnerHTML={{
                                __html:
                                  clientInviteBody?.content ||
                                  "<p>No event body found.</p>",
                              }}
                            />
                          </div>
                        </section>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </>
  );
}